/**
 * Motion kit — shared animation primitives for the whole portal.
 *
 * Drivers come from `@tamagui/config/v4` (see tamagui.config.ts): CSS
 * transitions on web (compositor-only, zero JS-thread cost) and the RN
 * Animated native driver on iOS/Android. Everything here animates only
 * `transform` + `opacity`, so scrolling/interaction perf is untouched.
 *
 * - `FadeIn`     — fade+rise on mount (enterStyle), for one-off blocks.
 * - `Enter`      — staggered fade+rise: pass `index` to cascade siblings.
 * - `AnimatePresence` — re-export for exit animations (drawers, overlays).
 *
 * Honors the OS "reduce motion" setting: staggering collapses and elements
 * mount in place.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { AccessibilityInfo } from 'react-native'
import { YStack, styled, type GetProps } from 'tamagui'

export { AnimatePresence } from 'tamagui'

// Module-level "reduce motion" flag — resolved async once, then kept live.
// (react-native-web maps this to `prefers-reduced-motion`.)
let reduceMotion = false
AccessibilityInfo.isReduceMotionEnabled?.()
  .then((v) => {
    reduceMotion = v
  })
  .catch(() => {})
AccessibilityInfo.addEventListener?.('reduceMotionChanged', (v) => {
  reduceMotion = v
})

export const prefersReducedMotion = () => reduceMotion

/** Fade + rise on mount. Cheap: pure enterStyle, no timers. */
export const FadeIn = styled(YStack, {
  name: 'FadeIn',
  animation: 'quicker',
  enterStyle: { opacity: 0, y: 10 },
  opacity: 1,
  y: 0,
})

/**
 * Staggered entrance — give each sibling its `index` and they cascade in
 * (fade + rise). State-driven (not enterStyle) because per-item delays must
 * work identically on the CSS and native drivers.
 */
export function Enter({
  index = 0,
  step = 45,
  from = 14,
  children,
  ...rest
}: {
  /** Position in the cascade — delay = 30 + index * step (ms). */
  index?: number
  /** Per-item delay increment (ms). */
  step?: number
  /** Initial rise offset (px). */
  from?: number
  children: ReactNode
} & GetProps<typeof YStack>) {
  const [shown, setShown] = useState(() => reduceMotion)
  useEffect(() => {
    if (reduceMotion) {
      setShown(true)
      return
    }
    const t = setTimeout(() => setShown(true), 30 + index * step)
    return () => clearTimeout(t)
  }, [index, step])
  return (
    <YStack animation="quicker" opacity={shown ? 1 : 0} y={shown ? 0 : from} {...rest}>
      {children}
    </YStack>
  )
}
